/**
 * 刀线孔位区域拆段公共工具。
 *
 * 注意：本文件不处理 DPR。调用方必须保证 points 和 holeRegions 使用同一坐标系。
 */

const EPSILON = 0.000001;

/**
 * 判断数值是否处于线段内部参数范围。
 * @param {number} value - 线段参数 t
 * @returns {boolean}
 */
function isSegmentT(value) {
  return value > EPSILON && value < 1 - EPSILON;
}

/**
 * 计算线段上的插值点。
 * @param {Array<number>} startPoint - 起点 [x, y]
 * @param {Array<number>} endPoint - 终点 [x, y]
 * @param {number} t - 线段参数
 * @returns {Array<number>} 插值点 [x, y]
 */
function getSegmentPoint(startPoint, endPoint, t) {
  return [
    startPoint[0] + (endPoint[0] - startPoint[0]) * t,
    startPoint[1] + (endPoint[1] - startPoint[1]) * t
  ];
}

/**
 * 判断两个点是否近似相同。
 * @param {Array<number>} pointA - 第一个点 [x, y]
 * @param {Array<number>} pointB - 第二个点 [x, y]
 * @returns {boolean}
 */
function isSamePoint(pointA, pointB) {
  return (
    !!pointA &&
    !!pointB &&
    Math.abs(pointA[0] - pointB[0]) <= EPSILON &&
    Math.abs(pointA[1] - pointB[1]) <= EPSILON
  );
}

/**
 * 向点集追加非重复点。
 * @param {Array<Array<number>>} points - 目标点集
 * @param {Array<number>} point - 待追加点 [x, y]
 * @returns {void}
 */
function pushUniquePoint(points, point) {
  const lastPoint = points[points.length - 1];

  // 相邻点一致时不重复追加，避免生成零长度路径段。
  if (isSamePoint(lastPoint, point)) {
    return;
  }

  points.push(point);
}

/**
 * 计算两点连线与圆形孔位的交点参数。
 * @param {Array<number>} startPoint - 起点 [x, y]
 * @param {Array<number>} endPoint - 终点 [x, y]
 * @param {Object} holeRegion - 圆形孔位区域
 * @returns {Array<number>} 交点在线段上的参数列表
 */
function getCircleIntersectionTList(startPoint, endPoint, holeRegion) {
  const dx = endPoint[0] - startPoint[0];
  const dy = endPoint[1] - startPoint[1];
  const fx = startPoint[0] - holeRegion.cx;
  const fy = startPoint[1] - holeRegion.cy;
  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - holeRegion.radius * holeRegion.radius;

  // 退化线段没有可用交点。
  if (!a) {
    return [];
  }

  const discriminant = b * b - 4 * a * c;

  // 判别式小于 0 表示线段所在直线不穿过圆。
  if (discriminant < 0) {
    return [];
  }

  const sqrtDiscriminant = Math.sqrt(Math.max(discriminant, 0));
  const firstT = (-b - sqrtDiscriminant) / (2 * a);
  const secondT = (-b + sqrtDiscriminant) / (2 * a);

  return [firstT, secondT].filter(isSegmentT);
}

/**
 * 将世界坐标点转换到旋转矩形局部坐标。
 * @param {Array<number>} point - 世界坐标点 [x, y]
 * @param {Object} holeRegion - 旋转矩形孔位区域
 * @returns {{x:number,y:number}} 局部坐标点
 */
function getRotatedRectLocalPoint(point, holeRegion) {
  const lx = point[0] - holeRegion.cx;
  const ly = point[1] - holeRegion.cy;
  const cos = Math.cos(-(holeRegion.rotationRad || 0));
  const sin = Math.sin(-(holeRegion.rotationRad || 0));

  return {
    x: lx * cos - ly * sin,
    y: lx * sin + ly * cos
  };
}

/**
 * 计算两点连线与轴对齐矩形的交点参数。
 * @param {{x:number,y:number}} startPoint - 局部起点
 * @param {{x:number,y:number}} endPoint - 局部终点
 * @param {number} halfWidth - 半宽
 * @param {number} halfHeight - 半高
 * @returns {Array<number>} 交点在线段上的参数列表
 */
function getAabbIntersectionTList(startPoint, endPoint, halfWidth, halfHeight) {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  let enterT = 0;
  let exitT = 1;
  const checks = [
    [-dx, startPoint.x + halfWidth],
    [dx, halfWidth - startPoint.x],
    [-dy, startPoint.y + halfHeight],
    [dy, halfHeight - startPoint.y]
  ];

  for (let i = 0; i < checks.length; i++) {
    const p = checks[i][0];
    const q = checks[i][1];

    // 平行且在矩形外侧时，线段不与矩形相交。
    if (Math.abs(p) <= EPSILON && q < 0) {
      return [];
    }

    // 平行且未越界时，该边界不限制当前线段。
    if (Math.abs(p) <= EPSILON) {
      continue;
    }

    const t = q / p;

    // 进入边界更新进入参数。
    if (p < 0) {
      enterT = Math.max(enterT, t);
    } else {
      exitT = Math.min(exitT, t);
    }

    // 进入参数超过退出参数时，线段不穿过矩形。
    if (enterT - exitT > EPSILON) {
      return [];
    }
  }

  return [enterT, exitT].filter(isSegmentT);
}

/**
 * 计算两点连线与旋转矩形孔位的交点参数。
 * @param {Array<number>} startPoint - 起点 [x, y]
 * @param {Array<number>} endPoint - 终点 [x, y]
 * @param {Object} holeRegion - 旋转矩形孔位区域
 * @returns {Array<number>} 交点在线段上的参数列表
 */
function getRotatedRectIntersectionTList(startPoint, endPoint, holeRegion) {
  const startLocal = getRotatedRectLocalPoint(startPoint, holeRegion);
  const endLocal = getRotatedRectLocalPoint(endPoint, holeRegion);

  return getAabbIntersectionTList(
    startLocal,
    endLocal,
    holeRegion.w / 2,
    holeRegion.h / 2
  );
}

/**
 * 获取线段与单个孔位区域的交点参数。
 * @param {Array<number>} startPoint - 起点 [x, y]
 * @param {Array<number>} endPoint - 终点 [x, y]
 * @param {Object} holeRegion - 孔位区域
 * @returns {Array<number>} 交点在线段上的参数列表
 */
function getHoleRegionIntersectionTList(startPoint, endPoint, holeRegion) {
  // 缺少孔位数据时没有交点。
  if (!holeRegion) {
    return [];
  }

  // 圆形孔位按线圆交点切分。
  if (
    (!holeRegion.kind || holeRegion.kind === 'circle') &&
    holeRegion.radius != null
  ) {
    return getCircleIntersectionTList(startPoint, endPoint, holeRegion);
  }

  // 旋转矩形孔位先转换到局部坐标，再按 AABB 裁剪求交。
  if (holeRegion.kind === 'rotatedRect') {
    return getRotatedRectIntersectionTList(startPoint, endPoint, holeRegion);
  }

  return [];
}

/**
 * 获取线段与孔位区域列表的去重交点参数。
 * @param {Array<number>} startPoint - 起点 [x, y]
 * @param {Array<number>} endPoint - 终点 [x, y]
 * @param {Array<Object>} holeRegions - 孔位区域列表
 * @returns {Array<number>} 排序后的交点参数列表
 */
function getOutlineSegmentBreakTList(startPoint, endPoint, holeRegions) {
  const breakTList = [0, 1];

  for (let i = 0; i < holeRegions.length; i++) {
    const tList = getHoleRegionIntersectionTList(
      startPoint,
      endPoint,
      holeRegions[i]
    );

    tList.forEach(function (t) {
      const hasSameT = breakTList.some(function (item) {
        return Math.abs(item - t) <= EPSILON;
      });

      // 已存在近似交点参数时不重复追加。
      if (hasSameT) {
        return;
      }

      breakTList.push(t);
    });
  }

  return breakTList.sort(function (tA, tB) {
    return tA - tB;
  });
}

/**
 * 判断点是否落在孔位区域内。
 * @param {Array<number>} point - 轮廓点 [x, y]
 * @param {Object} holeRegion - 孔位区域
 * @returns {boolean}
 */
export function isPointInHoleRegion(point, holeRegion) {
  // 缺少孔位数据时，不过滤当前点。
  if (!holeRegion) {
    return false;
  }

  // 圆形孔位按半径判断，覆盖 keyHole 和 perforation。
  if (
    (!holeRegion.kind || holeRegion.kind === 'circle') &&
    holeRegion.radius != null
  ) {
    const dx = point[0] - holeRegion.cx;
    const dy = point[1] - holeRegion.cy;
    return Math.sqrt(dx * dx + dy * dy) <= holeRegion.radius;
  }

  // 旋转矩形孔位先转到局部坐标，再按轴对齐矩形判断。
  if (holeRegion.kind === 'rotatedRect') {
    const localPoint = getRotatedRectLocalPoint(point, holeRegion);

    return (
      localPoint.x >= -holeRegion.w / 2 &&
      localPoint.x <= holeRegion.w / 2 &&
      localPoint.y >= -holeRegion.h / 2 &&
      localPoint.y <= holeRegion.h / 2
    );
  }

  return false;
}

/**
 * 判断点是否落在任一孔位区域内。
 * @param {Array<number>} point - 轮廓点 [x, y]
 * @param {Array<Object>} holeRegions - 孔位区域列表
 * @returns {boolean}
 */
export function isPointInAnyHoleRegion(point, holeRegions) {
  // 没有孔位区域时，不过滤当前点。
  if (!holeRegions || !holeRegions.length) {
    return false;
  }

  for (const holeRegion of holeRegions) {
    // 任一孔位命中后即可跳过当前点。
    if (isPointInHoleRegion(point, holeRegion)) {
      return true;
    }
  }

  return false;
}

/**
 * 结束当前开放片段。
 * @param {Array<{points:Array<Array<number>>,closed:boolean}>} segments - 绘制片段列表
 * @param {Array<Array<number>>} currentSegment - 当前片段点集
 * @returns {Array<Array<number>>} 新的当前片段点集
 */
function finishCurrentSegment(segments, currentSegment) {
  // 当前片段点数足够绘制时才保留。
  if (currentSegment.length >= 2) {
    segments.push({ points: currentSegment, closed: false });
  }

  return [];
}

/**
 * 合并跨数组边界的首尾开放片段。
 * @param {Array<{points:Array<Array<number>>,closed:boolean}>} segments - 绘制片段列表
 * @returns {Array<{points:Array<Array<number>>,closed:boolean}>}
 */
function mergeBoundarySegments(segments) {
  // 片段不足两个时没有跨边界合并需求。
  if (segments.length < 2) {
    return segments;
  }

  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const firstStartPoint = firstSegment.points[0];
  const lastEndPoint = lastSegment.points[lastSegment.points.length - 1];

  // 首尾片段在同一点衔接时，将末尾片段接到首段前面。
  if (isSamePoint(firstStartPoint, lastEndPoint)) {
    firstSegment.points = lastSegment.points.concat(
      firstSegment.points.slice(1)
    );
    segments.pop();
  }

  return segments;
}

/**
 * 根据孔位区域把闭合刀线拆成可绘制片段。
 * @param {Array<Array<number>>} points - 完整外轮廓点集
 * @param {Array<Object>} holeRegions - 孔位区域列表
 * @returns {Array<{points:Array<Array<number>>,closed:boolean}>}
 */
export function buildDrawableOutlineSegments(points, holeRegions) {
  // 没有原始点时不返回绘制片段。
  if (!points || !points.length) {
    return [];
  }

  // 没有孔位区域时，整条外轮廓按闭合路径绘制。
  if (!holeRegions || !holeRegions.length) {
    return [{ points: points, closed: true }];
  }

  const segments = [];
  let currentSegment = [];
  let hasHiddenInterval = false;

  for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
    const startPoint = points[pointIndex];
    const nextPointIndex = (pointIndex + 1) % points.length;
    const endPoint = points[nextPointIndex];
    const breakTList = getOutlineSegmentBreakTList(
      startPoint,
      endPoint,
      holeRegions
    );

    for (let breakIndex = 0; breakIndex < breakTList.length - 1; breakIndex++) {
      const startT = breakTList[breakIndex];
      const endT = breakTList[breakIndex + 1];

      // 零长度区间不参与绘制或拆段判断。
      if (endT - startT <= EPSILON) {
        continue;
      }

      const midT = (startT + endT) / 2;
      const midPoint = getSegmentPoint(startPoint, endPoint, midT);
      const startVisiblePoint = getSegmentPoint(startPoint, endPoint, startT);
      const endVisiblePoint = getSegmentPoint(startPoint, endPoint, endT);
      const isHidden = isPointInAnyHoleRegion(midPoint, holeRegions);

      // 穿过孔位的区间切断当前开放片段。
      if (isHidden) {
        hasHiddenInterval = true;
        currentSegment = finishCurrentSegment(segments, currentSegment);
        continue;
      }

      pushUniquePoint(currentSegment, startVisiblePoint);
      pushUniquePoint(currentSegment, endVisiblePoint);
    }
  }

  currentSegment = finishCurrentSegment(segments, currentSegment);

  // 没有任何隐藏区间时，仍按完整闭合轮廓绘制，保持旧行为。
  if (!hasHiddenInterval) {
    return [{ points: points, closed: true }];
  }

  return mergeBoundarySegments(segments).filter(function (segment) {
    return segment.points.length >= 2;
  });
}
